import { z } from 'zod';
import {
  ALL_LIFETIME_FREE_CATEGORIES,
  BillingCycle,
  BillingCycleSchema,
  CommercialMode,
  CommercialModeSchema,
  SubscriptionSchema,
  SubscriptionStatus,
  effectiveCommercialMode,
  isDirectPaying,
  isTestModeRecord,
  monthlyAmountCents,
  priceCentsToUsdCents,
  type Currency,
  type DirectPayingFields,
} from '@etiquetachef/types';
import { reportNonFatal } from '@/lib/errorReport';

/**
 * Leitura de receita dos dashboards do admin a partir de
 * `tenants/{tenantId}/billing/subscription` — o doc canônico do plano efetivo
 * (CLAUDE.md, "Fonte da verdade").
 *
 * Origem: #795 — 2026-10-03 14:12 +0200 (ADM-4 da auditoria geral).
 *
 * Por quê: `/dashboard/sales`, `/dashboard/customers` (top receita e coluna de
 * preço), `/dashboard/churn` e a tabela "Top contas" de `/dashboard/finance`
 * liam `tenants/{id}/billing/current` + `priceCents`, documento ÓRFÃO desde
 * 2026-05-19 (`e9692f87e` migrou todo escritor para `billing/subscription`).
 * Nenhuma Function, script ou cliente grava mais `billing/current`, então os
 * quatro painéis mostravam receita vazia, "0 novas assinaturas", churn 0 %,
 * preço "—" e "Top contas" vazia — sem erro e sem telemetria. Este módulo é o
 * lugar ÚNICO, no admin, onde o doc de assinatura vira número de receita, para
 * que os quatro painéis não voltem a divergir entre si.
 *
 * Contrato:
 *  - `parseSubscriptionDoc(path, raw)` → `null` se o caminho não é
 *    `tenants/{tid}/billing/subscription` (o collection group `billing` também
 *    traz `billing/usage`, `billing/current` legado etc.); `{ ok: false }` se o
 *    doc é de assinatura mas não passa no Zod (o chamador junta os descartes e
 *    reporta UMA vez com `reportSubscriptionDrops`); `{ ok: true }` com os
 *    campos que a régua lê.
 *  - `directMonthlyRevenue(fields, { anyStatus })` → contribuição mensal da
 *    receita DIRETA, ou `null` quando o tenant não conta.
 *  - `isDirectLiveSale(fields)` → venda direta e real (régua de "novas
 *    assinaturas", sem exigir pagamento).
 *  - `monthlyValue(fields)` → equivalente mensal do valor cobrado, SEM a
 *    régua de receita (linha de "criadas", que pode ainda não ter gateway).
 *  - `rankTopDirectRevenue(docs, limit)` → top N por receita mensal em USD eq.
 *  - Sem efeito colateral além do `reportNonFatal` (PostHog) dos descartes.
 *
 * Usado por: `dashboard-sales/useSalesMetrics.ts` (receita por país/plano,
 * novas assinaturas, cancelados, denominador do churn),
 * `dashboard-customers/useCustomers.ts` (aba top receita + coluna de preço de
 * todas as abas), `dashboard-churn/useChurnedTenants.ts` (preço e receita
 * perdida), `dashboard-finance/TopRevenueAccountsTable.tsx` — todos web
 * `apps/admin`.
 *
 * Arquitetura: espelha a régua das Functions
 * (`apps/functions/src/aggregations/revenuePredicates.ts`,
 * `directMonthlyRevenue`/`isDirectLiveBilling`) usando os MESMOS predicados de
 * `@etiquetachef/types` (`isDirectPaying`, `effectiveCommercialMode`,
 * `isTestModeRecord`, `monthlyAmountCents`, `priceCentsToUsdCents`) — só a
 * composição mora aqui, porque o admin não importa código das Functions.
 * Cortesia, demo e parceiro não são receita (#679 D15); test mode também não
 * (#463). Pricing congelado: o valor vem do doc do tenant, nunca do catálogo.
 *
 * Dados — `tenants/{tenantId}/billing/subscription` (escrito só pelas
 * Functions: webhooks Stripe/Asaas, `provisionTenantForSale`,
 * `dailyBillingTick`). Exemplo anonimizado, só com o que a régua lê:
 *   {
 *     "status": "ACTIVE",                 // SubscriptionStatus
 *     "currency": "EUR",                  // BRL | USD | EUR
 *     "amount": 34800,                    // LEGACY, CENTS por ciclo
 *     "total_amount_cents": 29900,        // soma de items[] — vence `amount`
 *     "billing_cycle": "yearly",          // ausente/desconhecido = monthly
 *     "plan_id": "STANDARD",
 *     "country_code": "IT",
 *     "commercial_mode": "DIRECT",        // ausente = DIRECT (ou o cupom decide)
 *     "livemode": true,                   // só `false` é test mode
 *     "external_provider": "stripe",
 *     "external_subscription_id": "sub_000000000000",
 *     "created_at": 1790000000000         // ms
 *   }
 * Schema: `SubscriptionSchema` em `packages/types/src/schemas.ts` — a
 * projeção abaixo reaproveita os campos de lá e afrouxa só o que a régua das
 * Functions também tolera (ver `SubscriptionRevenueFieldsSchema`).
 *
 * Falhas conhecidas:
 *  - 2026-05-19 → 2026-10-03 (#795, ADM-4): os quatro painéis liam o doc
 *    órfão `billing/current` e ficaram vazios por 4 meses sem nenhum aviso.
 *
 * Dicas:
 *  - Valor: `total_amount_cents ?? amount`. `amount` é LEGACY e fica velho
 *    quando a remoção agendada de módulo (`dailyBillingTick` →
 *    `pendingRemoval.ts`) regravava só `total_amount_cents` — corrigido na
 *    #795 (ARQ-10): o escritor passou a espelhar `amount`, e o agregado das
 *    Functions (`directMonthlyRevenue` em `revenuePredicates.ts`) usa a MESMA
 *    régua `total_amount_cents ?? amount` desta tela. Docs que já divergiam
 *    antes da correção só se alinham na próxima escrita (sem backfill).
 *  - Ordenação por receita é em MEMÓRIA: o valor mensal em USD depende do
 *    ciclo e da moeda, que nenhum campo indexável carrega — não volte a usar
 *    `orderBy('priceCents')` (o índice `status+priceCents` foi removido).
 *
 * Issues: #795 (ADM-4), #679 (D15 — régua da receita direta), #463 (test
 * mode fora dos relatórios), #738 (cortesia/demo como tipo comercial).
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-4) criado; substitui as leituras de
 *    `billing/current` dos quatro painéis.
 */

/** Caminho do singleton de assinatura; o grupo `billing` tem outros docs. */
const SUBSCRIPTION_PATH_RE = /^tenants\/([^/]+)\/billing\/subscription$/;

/**
 * O `tenantId` de `tenants/{tid}/billing/subscription`, ou `null`.
 *
 * Origem: #795 — 2026-10-03 15:00 +0200 (ADM-4, commit 76bc108ad).
 * Usado por: `parseSubscriptionDoc` (aqui) e `subscriptionRevenue.test.ts`.
 */
export function tenantIdFromSubscriptionPath(path: string): string | null {
  const m = SUBSCRIPTION_PATH_RE.exec(path);
  return m?.[1] ?? null;
}

const S = SubscriptionSchema.shape;

/**
 * Projeção do `SubscriptionSchema` com SÓ os campos que a régua de receita lê.
 *
 * Os campos que decidem SE o tenant conta e QUANTO (`status`, `currency`,
 * `amount`, `total_amount_cents`, `commercial_mode`) usam o schema canônico ou
 * o enum dele: valor de tipo errado/desconhecido descarta o doc E vira report
 * (antes sumia sem rastro). As exceções, cada uma pelo mesmo motivo da régua
 * das Functions, que lê o doc cru:
 *  - `billing_cycle` desconhecido/ausente → MONTHLY (`cycleOf` das Functions;
 *    dividir por 1 é a leitura que não inventa receita);
 *  - `commercial_mode` NÃO usa o `.catch('DIRECT')` do canônico: lá o lado
 *    fechado é o gate de pagamento; aqui, promover um modo desconhecido a
 *    DIRECT inventaria receita. Desconhecido descarta e reporta;
 *  - ids do gateway como `string` livre: `isRecognizedGateway` decide, e o
 *    regex do canônico descartaria receita real se o formato mudar;
 *  - `livemode` aceita `null` (tratado como ausente = live, ver
 *    `isTestModeRecord`).
 */
const SubscriptionRevenueFieldsSchema = z.object({
  status: S.status,
  currency: S.currency,
  amount: S.amount.optional(),
  total_amount_cents: S.total_amount_cents,
  billing_cycle: BillingCycleSchema.optional().catch(undefined),
  plan_id: z.string().nullish(),
  country_code: z.string().nullish(),
  livemode: z.boolean().nullish(),
  commercial_mode: CommercialModeSchema.nullish(),
  discount: z
    .object({
      is_lifetime_free: z.boolean().nullish(),
      // Categoria desconhecida com `is_lifetime_free: true` continua COURTESY
      // em `effectiveCommercialMode` — tratá-la como `null` dá o mesmo modo.
      lifetime_free_category: z.enum(ALL_LIFETIME_FREE_CATEGORIES).nullish().catch(null),
    })
    .nullish(),
  external_provider: z.string().nullish(),
  external_subscription_id: z.string().nullish(),
  created_at: z.number().nullish(),
});
export type SubscriptionRevenueFields = z.infer<typeof SubscriptionRevenueFieldsSchema>;

/** Um doc de assinatura descartado no parse — vai para o report agregado. */
export type SubscriptionDrop = { tenantId: string; issues: string[] };

export type ParsedSubscription =
  | { ok: true; tenantId: string; fields: SubscriptionRevenueFields }
  | { ok: false; tenantId: string; drop: SubscriptionDrop };

/**
 * Valida um doc do collection group `billing`. `null` = não é o singleton de
 * assinatura (ignorar em silêncio é o certo: `billing/usage` e o legado
 * `billing/current` caem no mesmo grupo).
 *
 * Origem: #795 — 2026-10-03 15:00 +0200 (ADM-4, commit 76bc108ad).
 * Usado por: `parseSubscriptionDocs` (aqui), `dashboard-sales/useSalesMetrics.ts`,
 * `dashboard-customers/useCustomers.ts`, `dashboard-churn/useChurnedTenants.ts` (`apps/admin`).
 *
 * Dicas: o `drop` leva só caminho + código do Zod — a mensagem pode ecoar valor do doc
 * (dado de cliente), e o drop vai para o PostHog.
 *
 * Histórico:
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 */
export function parseSubscriptionDoc(path: string, raw: unknown): ParsedSubscription | null {
  const tenantId = tenantIdFromSubscriptionPath(path);
  if (!tenantId) return null;
  const parsed = SubscriptionRevenueFieldsSchema.safeParse(raw);
  if (parsed.success) return { ok: true, tenantId, fields: parsed.data };
  // Só caminho + código: a mensagem do Zod pode ecoar o valor do campo.
  const issues = parsed.error.issues
    .slice(0, 5)
    .map((i) => `${i.path.map(String).join('.')}:${i.code}`);
  return { ok: false, tenantId, drop: { tenantId, issues } };
}

/**
 * Parse em lote de snapshots (`{ ref.path, data() }`), acumulando os
 * descartes em `drops`. Devolve `tenantId → campos`.
 *
 * Origem: #795 — 2026-10-03 15:00 +0200 (ADM-4, commit 76bc108ad).
 * Usado por: `TopRevenueAccountsTable.tsx`, `dashboard-customers/useCustomers.ts`,
 * `dashboard-sales/useSalesMetrics.ts` (`apps/admin`).
 */
export function parseSubscriptionDocs(
  docs: readonly { ref: { path: string }; data: () => unknown }[],
  drops: SubscriptionDrop[],
): Map<string, SubscriptionRevenueFields> {
  const out = new Map<string, SubscriptionRevenueFields>();
  for (const d of docs) {
    const parsed = parseSubscriptionDoc(d.ref.path, d.data());
    if (parsed === null) continue;
    if (parsed.ok) out.set(parsed.tenantId, parsed.fields);
    else drops.push(parsed.drop);
  }
  return out;
}

/**
 * Reporta, UMA vez por carga, os docs de assinatura descartados no parse.
 * Um evento por doc inundaria o PostHog se um campo mudar de formato em massa;
 * a amostra de 5 basta para achar o doc.
 *
 * Origem: #795 — 2026-10-03 15:00 +0200 (ADM-4, commit 76bc108ad).
 * Contrato: evento PostHog `admin_subscription_doc_dropped` (`reportNonFatal`) com
 * `{ context, count, sample: até 5 × { tenantId, issues } }`; lista vazia não reporta.
 *
 * Usado por: `TopRevenueAccountsTable.tsx`, `useSalesMetrics.ts`, `useCustomers.ts`,
 * `useChurnedTenants.ts` (`apps/admin`).
 */
export function reportSubscriptionDrops(context: string, drops: readonly SubscriptionDrop[]): void {
  if (drops.length === 0) return;
  reportNonFatal('admin_subscription_doc_dropped', {
    context,
    count: drops.length,
    sample: drops.slice(0, 5),
  });
}

/** Os campos da régua no formato dos predicados de `@etiquetachef/types`. */
function predicateFields(f: SubscriptionRevenueFields): DirectPayingFields {
  return {
    status: f.status,
    commercial_mode: f.commercial_mode ?? undefined,
    discount: f.discount ?? undefined,
    external_provider: f.external_provider ?? undefined,
    external_subscription_id: f.external_subscription_id ?? undefined,
    livemode: f.livemode ?? undefined,
  };
}

/**
 * Valor cobrado por ciclo, em cents: `total_amount_cents ?? amount` (mesma
 * leitura de `computeSubscriptionReceivables`/`BillingCard`). `null` sem
 * valor positivo — tenant de valor zero não é receita.
 */
function chargedCycleCents(f: SubscriptionRevenueFields): number | null {
  const v = f.total_amount_cents ?? f.amount;
  return typeof v === 'number' && v > 0 ? v : null;
}

export type DirectRevenue = { currency: Currency; monthlyCents: number; usdEqCents: number };

/**
 * Equivalente mensal (anual ÷ 12, trimestral ÷ 3 — `monthlyAmountCents`, régua
 * do MRR, #679 D15) do valor cobrado por ciclo, na moeda nativa e em USD eq.
 * NÃO aplica a régua de receita direta — quem precisa dela usa
 * `directMonthlyRevenue`; a tabela de "criadas" usa este direto porque a venda
 * recém-provisionada ainda não tem gateway. `null` sem valor positivo.
 *
 * Origem: #795 — 2026-10-03 15:00 +0200 (ADM-4, commit 76bc108ad).
 * Usado por: `directMonthlyRevenue` (aqui) e `dashboard-sales/useSalesMetrics.ts`.
 *
 * Dados: devolve CENTAVOS — `monthlyCents` na moeda nativa e `usdEqCents` convertido por
 * `priceCentsToUsdCents` (taxa fixa `FX_TO_USD` de `@etiquetachef/types`, não cotação).
 */
export function monthlyValue(f: SubscriptionRevenueFields): DirectRevenue | null {
  const cycleCents = chargedCycleCents(f);
  if (cycleCents === null) return null;
  const monthlyCents = monthlyAmountCents(cycleCents, f.billing_cycle ?? BillingCycle.MONTHLY);
  return { currency: f.currency, monthlyCents, usdEqCents: priceCentsToUsdCents(monthlyCents, f.currency) };
}

/**
 * Contribuição mensal do tenant para a receita DIRETA, ou `null` quando ele
 * não conta: não-DIRECT (cortesia, demo, parceiro — inclusive os legados só
 * com cupom), fora de ACTIVE, sem gateway reconhecido, test mode ou valor
 * zero. Mesma régua de `directMonthlyRevenue` das Functions.
 *
 * `anyStatus` serve ao churn: o tenant que saiu já não está ACTIVE, mas o que
 * ele levou embora é o mesmo mensal que contava antes. Implementado forçando
 * `status: ACTIVE` no predicado canônico em vez de recompor os outros três
 * termos — assim a régua continua tendo uma fonte só.
 *
 * Origem: #795 — 2026-10-03 15:00 +0200 (ADM-4, commit 76bc108ad).
 * Usado por: `rankTopDirectRevenue` (aqui), `useSalesMetrics.ts`, `useCustomers.ts`,
 * `useChurnedTenants.ts` (`apps/admin`); espelho de
 * `apps/functions/src/aggregations/revenuePredicates.ts`.
 */
export function directMonthlyRevenue(
  f: SubscriptionRevenueFields,
  opts: { anyStatus?: boolean } = {},
): DirectRevenue | null {
  const fields = predicateFields(f);
  const counts = isDirectPaying(
    opts.anyStatus ? { ...fields, status: SubscriptionStatus.ACTIVE } : fields,
  );
  return counts ? monthlyValue(f) : null;
}

/**
 * Venda direta e real, SEM exigir pagamento — régua de "novas assinaturas"
 * (`isDirectLiveBilling` das Functions, que alimenta `sales`/`createdToday`):
 * a venda assistida do CRM cria o doc antes do pagamento, e demo/cortesia
 * criados no período não são venda.
 *
 * Origem: #795 — 2026-10-03 15:00 +0200 (ADM-4, commit 76bc108ad).
 * Usado por: `dashboard-sales/useSalesMetrics.ts` (`apps/admin`).
 */
export function isDirectLiveSale(f: SubscriptionRevenueFields): boolean {
  const fields = predicateFields(f);
  return effectiveCommercialMode(fields) === CommercialMode.DIRECT && !isTestModeRecord(fields);
}

export type RankedRevenue = DirectRevenue & { tenantId: string };

/**
 * Top N tenants por receita direta mensal (USD eq.), a partir dos docs do
 * collection group `billing` com `status == ACTIVE`. Empate: `tenantId`, para
 * a ordem não depender da ordem do snapshot.
 *
 * Origem: #795 — 2026-10-03 15:00 +0200 (ADM-4, commit 76bc108ad).
 * Usado por: `TopRevenueAccountsTable.tsx` e `dashboard-customers/useCustomers.ts`.
 */
export function rankTopDirectRevenue(
  subs: ReadonlyMap<string, SubscriptionRevenueFields>,
  limit: number,
): RankedRevenue[] {
  const ranked: RankedRevenue[] = [];
  for (const [tenantId, f] of subs) {
    const revenue = directMonthlyRevenue(f);
    if (revenue) ranked.push({ tenantId, ...revenue });
  }
  ranked.sort((a, b) => b.usdEqCents - a.usdEqCents || a.tenantId.localeCompare(b.tenantId));
  return ranked.slice(0, limit);
}
